import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Select,
  Button,
  message,
  List,
  Tag,
  Avatar,
  Empty,
  Spin,
  Alert,
} from 'antd';
import {
  SaveOutlined,
  UserOutlined,
  SafetyOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import LayoutWrapper from '@iso/components/utility/layoutWrapper.js';
import Box from '@iso/components/utility/box';
import PageHeader from '@iso/components/utility/pageHeader';
import { propertyRoleService, userService } from '@iso/services';
import { useSelector } from 'react-redux';
import RoleAssignmentWrapper from './RoleAssignment.styles';

const { Option } = Select;

const RoleAssignment = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [roleTypes, setRoleTypes] = useState([]);
  const currentProperty = useSelector((state) => state.property.currentProperty);
  const [property, setProperty] = useState(null);

  useEffect(() => {
    // Get property from Redux or localStorage
    const storedProperty = localStorage.getItem('active_property');
    const activeProperty = currentProperty || (storedProperty ? JSON.parse(storedProperty) : null);

    if (activeProperty && activeProperty.id) {
      setProperty(activeProperty);
      loadData(activeProperty.id);
    } else {
      setInitialLoading(false);
      message.error('No property selected. Please select a property first.');
    }
  }, [currentProperty]);

  const loadData = async (propertyId) => {
    try {
      setInitialLoading(true);
      const [usersRes, rolesRes, roleTypesRes] = await Promise.all([
        userService.getUserForProperty(propertyId),
        propertyRoleService.getAllRoles(propertyId),
        propertyRoleService.getRoleTypes(),
      ]);

      if (usersRes.code === '0000') {
        setUsers(usersRes.users || []);
      }

      if (rolesRes.code === '0000') {
        setRoles(rolesRes.roles || []);

        // Set initial form values from existing role assignments
        const initialValues = {};
        (rolesRes.roles || []).forEach(role => {
          initialValues[role.role_type] = role.users.map(u => u.id);
        });
        form.setFieldsValue(initialValues);
      }

      if (roleTypesRes.code === '0000') {
        setRoleTypes(roleTypesRes.role_types || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      message.error('Failed to load role assignments');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    if (!property || !property.id) {
      message.error('No property selected');
      return;
    }

    try {
      setLoading(true);

      // Update each role assignment
      const promises = roleTypes.map(roleType => {
        const userIds = values[roleType.value] || [];
        return propertyRoleService.assignUsersToRole(
          property.id,
          roleType.value,
          userIds
        );
      });

      await Promise.all(promises);

      message.success('Role assignments updated successfully');
      loadData(property.id);
    } catch (error) {
      console.error('Error updating role assignments:', error);
      message.error('Failed to update role assignments');
    } finally {
      setLoading(false);
    }
  };

  const getRoleIcon = (roleType) => {
    if (roleType.includes('safety')) {
      return <SafetyOutlined />;
    } else if (roleType.includes('manager')) {
      return <TeamOutlined />;
    }
    return <UserOutlined />;
  };

  const getRoleDescription = (roleType) => {
    const descriptions = {
      'group_safety_manager': 'Receives hazard escalation notifications at Level 3',
      'property_safety_manager': 'Receives hazard escalation notifications at Level 2 (if assigned)',
      'regional_manager': 'Receives hazard escalation notifications at Level 3',
      'gm': 'Receives hazard escalation notifications at Level 2',
      'managing_director': 'Managing Director - receives incident notifications and has Safety Admin powers',
      'site_safety_officer': 'Receives hazard escalation notifications at Level 2 (if assigned)',
    };
    return descriptions[roleType] || '';
  };

  const getCurrentAssignments = (roleType) => {
    const role = roles.find(r => r.role_type === roleType);
    return role ? role.users : [];
  };

  if (initialLoading) {
    return (
      <LayoutWrapper>
        <PageHeader>Safety Role Assignment</PageHeader>
        <Box>
          <Card loading={true} />
        </Box>
      </LayoutWrapper>
    );
  }

  if (!property) {
    return (
      <LayoutWrapper>
        <PageHeader>Safety Role Assignment</PageHeader>
        <Box>
          <Card>
            <p>No property selected. Please select a property from the properties list.</p>
          </Card>
        </Box>
      </LayoutWrapper>
    );
  }

  return (
    <RoleAssignmentWrapper>
      <LayoutWrapper>
        <Box>
          <div className="page-header-wrapper">
            <h2 className="page-title">Safety Role Assignment - {property.name}</h2>
          </div>

          <Card title="Assign Users to Safety Roles" className="main-card">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
            >
              {roleTypes.map(roleType => (
                <Card
                  key={roleType.value}
                  type="inner"
                  title={
                    <div className="role-header">
                      {getRoleIcon(roleType.value)}
                      <span className="role-title">{roleType.label}</span>
                    </div>
                  }
                  className="role-card"
                  extra={
                    <Tag color="blue">
                      {getCurrentAssignments(roleType.value).length} assigned
                    </Tag>
                  }
                >
                  <p className="role-description">
                    {getRoleDescription(roleType.value)}
                  </p>

                  <Form.Item
                    name={roleType.value}
                    label={`Select users for ${roleType.label}`}
                  >
                    <Select
                      mode="multiple"
                      placeholder="Select users (multiple allowed)"
                      optionFilterProp="label"
                      showSearch
                      allowClear
                      className="user-select"
                    >
                      {users.map(user => (
                        <Option key={user.id} value={user.id} label={`${user.name} (${user.email})`}>
                          <Avatar size="small" icon={<UserOutlined />} style={{ marginRight: 8 }} />
                          {user.name} ({user.email})
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  {getCurrentAssignments(roleType.value).length > 0 && (
                    <div className="current-assignments">
                      <div className="assignments-label">Currently Assigned:</div>
                      <List
                        size="small"
                        dataSource={getCurrentAssignments(roleType.value)}
                        renderItem={user => (
                          <List.Item>
                            <List.Item.Meta
                              avatar={<Avatar icon={<UserOutlined />} />}
                              title={user.name}
                              description={user.email}
                            />
                          </List.Item>
                        )}
                      />
                    </div>
                  )}
                </Card>
              ))}

              <Form.Item style={{ marginTop: 24 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={loading}
                  size="large"
                  className="save-button"
                >
                  Save All Role Assignments
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Box>
      </LayoutWrapper>
    </RoleAssignmentWrapper>
  );
};

export default RoleAssignment;
